import { ColumnDef, FormatsState, DisplayOptions, Stage } from './types';
import { COLOR_PRESETS, cellRule, matchedRules, rowFormat } from './formats';
import { ConditionalIcon } from './ConditionalIcon';
import { cn } from '@/lib/utils';

interface OperationCardProps<T> {
  row: T;
  columns: ColumnDef<T>[];
  formats: FormatsState;
  display: DisplayOptions;
  compact?: boolean;
  titleColumnId?: string;
  stageAccessor?: (row: T) => Stage | undefined;
}

const STAGE_BADGE: Record<Stage, string> = {
  Kitting: 'bg-blue-50 text-blue-700 border-blue-200',
  Fabric: 'bg-purple-50 text-purple-700 border-purple-200',
  Metal: 'bg-amber-50 text-amber-700 border-amber-200',
  Assembly: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export function OperationCard<T>({
  row,
  columns,
  formats,
  display,
  compact = false,
  titleColumnId,
  stageAccessor,
}: OperationCardProps<T>) {
  const visibleColumns = columns.filter((c) => !display.hiddenColumns.has(c.id));
  const titleColumn =
    (titleColumnId && visibleColumns.find((c) => c.id === titleColumnId)) ||
    visibleColumns[0] ||
    null;
  const stage = stageAccessor?.(row);
  const stageVisible = !!stage;
  const bodyColumns = visibleColumns.filter((c) => c.id !== titleColumn?.id);

  const rowColor = rowFormat(row, columns, formats);
  const rowPalette = rowColor ? COLOR_PRESETS[rowColor] : null;
  const chipMatches = matchedRules(row, columns, formats).filter(({ rule }) => !rule.applyToRow);

  const padding =
    display.rowSpacing === 'loose'
      ? compact
        ? 'px-3 py-3'
        : 'px-5 py-4'
      : compact
        ? 'px-3 py-2'
        : 'px-4 py-2.5';

  const bodyGrid = compact ? 'grid-cols-[auto_1fr]' : 'grid-cols-[auto_1fr_auto_1fr]';

  return (
    <div
      className={cn(
        'rounded-lg border transition-all',
        padding,
        rowPalette
          ? cn(rowPalette.bg, rowPalette.border)
          : 'bg-white border-slate-200 hover:border-slate-300'
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {stageVisible && stage && (
          <span
            className={cn(
              'font-medium rounded-md border',
              compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5',
              STAGE_BADGE[stage]
            )}
          >
            {stage}
          </span>
        )}
        {titleColumn && (
          <h3
            className={cn(
              'font-semibold tracking-tight',
              compact ? 'text-sm' : 'text-base',
              rowPalette ? rowPalette.text : 'text-slate-900'
            )}
          >
            {titleColumn.accessor(row) || '—'}
          </h3>
        )}
      </div>

      {bodyColumns.length > 0 && (
        <div
          className={cn(
            'mt-1.5 grid gap-x-3 gap-y-0.5',
            compact ? 'text-[11px]' : 'text-xs',
            bodyGrid
          )}
        >
          {bodyColumns.map((col) => {
            const rule = cellRule(row, col, formats);
            const bgClass = rule?.bgColor
              ? COLOR_PRESETS[rule.bgColor].bg
              : rule?.color
                ? COLOR_PRESETS[rule.color].bg
                : '';
            const textClass = rule?.textColor
              ? COLOR_PRESETS[rule.textColor].text
              : rule?.color
                ? COLOR_PRESETS[rule.color].text
                : '';
            const value = col.accessor(row);
            const baseContent = value || <span className="text-slate-300">—</span>;
            return (
              <div key={col.id} className="contents">
                <div
                  className={cn(
                    'uppercase tracking-wider font-medium',
                    rowPalette ? rowPalette.text : 'text-slate-400'
                  )}
                >
                  {col.label}
                </div>
                <div
                  className={cn(
                    'font-medium truncate inline-flex items-center gap-1.5',
                    bgClass || textClass
                      ? cn('px-1.5 -mx-1.5 rounded', bgClass, textClass)
                      : rowPalette
                        ? rowPalette.text
                        : 'text-slate-900'
                  )}
                >
                  {rule?.icon && (
                    <ConditionalIcon
                      name={rule.icon}
                      color={rule.iconColor ?? rule.color}
                    />
                  )}
                  {(!rule?.icon || !rule.hideValue) && <span className="truncate">{baseContent}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {chipMatches.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {chipMatches.map(({ col, rule }) => {
            const bg = rule.bgColor ?? rule.color ?? 'gray';
            const text = rule.textColor ?? rule.color ?? 'gray';
            const p = COLOR_PRESETS[bg];
            const tp = COLOR_PRESETS[text];
            return (
              <span
                key={col.id}
                className={cn(
                  'font-medium rounded-md border',
                  compact ? 'text-[10px] px-1.5 py-0.5' : 'text-[11px] px-2 py-0.5',
                  p.bg,
                  tp.text,
                  p.border
                )}
              >
                {col.label}: {col.accessor(row)}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
